import { Link } from 'react-router-dom';
import beanComplete from '../assets/example-work/bean-complete.jpg';
import beanProgress from '../assets/example-work/bean-progress.jpg';
import foxholeDuring1 from '../assets/example-work/foxhole-during-construction1.png';
import foxholeDuring2 from '../assets/example-work/foxhole-during-construction2.png';
import foxholeOverview from '../assets/example-work/foxhole-overview.png';
import foxholeCompleted from '../assets/example-work/foxhole-completed-project.png';
import isherwoodProgress from '../assets/example-work/isherwood-progress.jpg';
import vincentProgress from '../assets/example-work/vincent-progress-a.jpg';
import vincentStructure from '../assets/example-work/vincent-structure.jpg';
import { DJM_BUILD_URL, INSTAGRAM_URL } from '../site';

const PROJECTS = [
  {
    id: 'complete-roof',
    title: 'Complete roof replacement & extension',
    area: 'Near Halwill, Devon',
    summary:
      'Full roof replacement together with porch and extension works — from scaffolding and open roof structure through to the finished slate roof. Client references available on request.',
    images: [
      {
        src: foxholeDuring1,
        alt: 'House under scaffolding during roof replacement',
        caption: 'During construction',
      },
      {
        src: foxholeDuring2,
        alt: 'Open roof structure and rafters during rebuild',
        caption: 'During construction',
      },
      {
        src: foxholeOverview,
        alt: 'Finished house with new slate roof and porch',
        caption: 'Completed',
      },
      {
        src: foxholeCompleted,
        alt: 'Finished rear elevation with new roof',
        caption: 'Completed',
      },
    ],
  },
  {
    id: 'bungalow-extension',
    title: 'Bungalow extension & remodel',
    area: 'Near Holsworthy, Devon',
    summary:
      'Side extension creating a new master bedroom with en-suite, full bathroom refit in the existing bungalow, plus fencing and patio works — delivered from groundworks through to finish.',
    images: [
      {
        src: beanProgress,
        alt: 'Bungalow extension under construction with scaffolding',
        caption: 'During construction',
      },
      {
        src: beanComplete,
        alt: 'Completed single-storey bungalow extension',
        caption: 'Completed',
      },
    ],
  },
  {
    id: 'family-home-extension',
    title: 'Family home extension & outbuilding',
    area: 'Rural Holsworthy, Devon',
    summary:
      'Large living extension for kitchen, dining and lounge, remodel of the original kitchen into a separate sitting room, new patios, and an ancillary outbuilding with bedroom and bathroom for guests and business use.',
    images: [
      {
        src: isherwoodProgress,
        alt: 'Timber-clad house extension under scaffolding',
        caption: 'During construction',
      },
    ],
  },
  {
    id: 'farmhouse-extension',
    title: 'Two-storey farmhouse extension',
    area: 'Near Bude, Cornwall',
    summary:
      'Double-storey addition providing two extra bedrooms and a new lounge, with structural blockwork, roof carpentry, and careful integration into the existing farmhouse.',
    images: [
      {
        src: vincentProgress,
        alt: 'Two-storey farmhouse extension in progress',
        caption: 'During construction',
      },
      {
        src: vincentStructure,
        alt: 'Roof structure being built on residential extension',
        caption: 'During construction',
      },
    ],
  },
];

function ExampleWorkPage() {
  return (
    <main className="Page Page--subpage Page--example-work">
      <div className="Page-content">
        <h1 className="Page-title">Example work</h1>
        <p className="Page-intro">
          A selection of residential projects delivered by founder Daniel Martin
          through DJM Roofing and Build — new builds, extensions, and major
          remodels across Devon and Cornwall. Where we have them, photos show
          both during construction and completed. Locations are generalised here
          to respect client privacy; fuller project photography is on{' '}
          <a href={DJM_BUILD_URL} target="_blank" rel="noopener noreferrer">
            djmbuild.co.uk
          </a>{' '}
          and Instagram.
        </p>

        <div className="ProjectList">
          {PROJECTS.map((project) => (
            <article key={project.id} className="ProjectCard">
              <header className="ProjectCard-header">
                <h2 className="ProjectCard-title">{project.title}</h2>
                <p className="ProjectCard-area">{project.area}</p>
              </header>
              <p className="ProjectCard-summary">{project.summary}</p>
              <div
                className={
                  project.images.length >= 4
                    ? 'BeforeAfter BeforeAfter--quad'
                    : project.images.length > 1
                      ? 'BeforeAfter'
                      : 'BeforeAfter BeforeAfter--single'
                }
                role="group"
                aria-label={project.title}
              >
                {project.images.map((image) => (
                  <figure key={image.src} className="BeforeAfter-item">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="BeforeAfter-image"
                      loading="lazy"
                    />
                    {image.caption ? (
                      <figcaption className="BeforeAfter-caption">
                        {image.caption}
                      </figcaption>
                    ) : null}
                  </figure>
                ))}
              </div>
            </article>
          ))}
        </div>

        <section className="ProjectMore" aria-labelledby="more-projects-heading">
          <h2 id="more-projects-heading" className="ProjectMore-heading">
            Further completed schemes
          </h2>
          <ul className="ProjectMore-list">
            <li>
              <strong>Bedroom suite &amp; outbuilding</strong> — Flexbury / Bude
              area: extension with en-suite plus a new outbuilding.
            </li>
            <li>
              <strong>Four-bedroom new build</strong> — Holsworthy area: full
              house delivered from start to finish.
            </li>
            <li>
              <strong>Twin extensions &amp; decking</strong> — Pyworthy area:
              two new extensions with outdoor living space.
            </li>
          </ul>
        </section>

        <p className="Page-note">
          These examples sit alongside many more roofs, renovations, and
          extensions. For more completed schemes and day-to-day site photos, see{' '}
          <a href={DJM_BUILD_URL} target="_blank" rel="noopener noreferrer">
            djmbuild.co.uk
          </a>{' '}
          or follow DJM Roofing and Build on Instagram.
        </p>

        <div className="Page-actions">
          <a
            href={DJM_BUILD_URL}
            className="Button Button--secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit DJM Roofing and Build
          </a>
          <a
            href={INSTAGRAM_URL}
            className="Button Button--secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            See more on Instagram
          </a>
          <Link to="/" className="Button">
            Back to home
          </Link>
        </div>
      </div>
    </main>
  );
}

export default ExampleWorkPage;
